import { Box, Fade } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';

import type { FieldConfig } from '../config';
import type { FieldRenderProps, SectionKey } from '../lease-details.types';
import { useVisibilityRule } from '../hooks';
import {
  AddressField,
  CheckboxField,
  DateField,
  MoneyField,
  OptionBoxField,
  ReadOnlyField,
  SelectField,
  SwitchField,
  TextField,
} from './kinds';
import {
  buildPath,
  resolveFieldErrorHelperText,
  resolveFieldHelperText,
  resolveFieldLabel,
} from './utils';

type FieldDispatcherProps = {
  name: string;
  field: FieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  inheritedDisabled: boolean;
};

export const FieldDispatcher = ({
  name,
  field,
  sectionKey,
  arrayIndex,
  inheritedDisabled,
}: FieldDispatcherProps) => {
  const path = buildPath(sectionKey, name, arrayIndex);
  const isVisible = useVisibilityRule(field.visible);
  const requiredResolved = useVisibilityRule(field.required ?? false);
  const disabledResolved = useVisibilityRule(field.disabled ?? false);
  const disabled = inheritedDisabled || disabledResolved;

  const resolvedLabel = resolveFieldLabel(field.label, arrayIndex);
  const resolvedHelperText = resolveFieldHelperText(field.helperText, arrayIndex);
  const resolvedErrorHelperText = resolveFieldErrorHelperText(field.errorHelperText, arrayIndex);

  const renderInput = (): ReactNode => {
    if (field.component) {
      // Cast to the unknown-typed render-props shape so the discriminated union
      // doesn't force `defaultValue` into the intersection of every variant's
      // value type. The consumer's component declares its own TValue and reads
      // `defaultValue` accordingly.
      const Override = field.component as ComponentType<FieldRenderProps>;
      return (
        <Override
          name={path}
          sectionKey={sectionKey}
          arrayIndex={arrayIndex}
          required={requiredResolved}
          disabled={disabled}
          label={resolvedLabel}
          placeholder={field.placeholder}
          helperText={resolvedHelperText}
          defaultValue={field.defaultValue}
        />
      );
    }

    return renderByKind();
  };

  // Fade fields animate in on appear only; when the rule hides them they're
  // removed immediately (no fade-out), matching the legacy form's
  // conditionally-rendered `<Fade in>`.
  if (field.fade) {
    return isVisible ? (
      <Fade in appear>
        <Box>{renderInput()}</Box>
      </Fade>
    ) : null;
  }

  if (!isVisible) return null;
  return renderInput();

  function renderByKind(): ReactNode {
    switch (field.kind) {
      case 'text':
        return (
          <TextField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
            helperText={resolvedHelperText}
            errorHelperText={resolvedErrorHelperText}
          />
        );
      case 'money':
        return (
          <MoneyField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
            helperText={resolvedHelperText}
            errorHelperText={resolvedErrorHelperText}
          />
        );
      case 'date':
        return (
          <DateField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
            helperText={resolvedHelperText}
          />
        );
      case 'select':
        return (
          <SelectField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
            helperText={resolvedHelperText}
          />
        );
      case 'text-readonly':
        return <ReadOnlyField path={path} field={field} label={resolvedLabel} />;
      case 'option-box':
        return (
          <OptionBoxField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
          />
        );
      case 'switch':
        return (
          <SwitchField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            disabled={disabled}
          />
        );
      case 'checkbox':
        return (
          <CheckboxField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            disabled={disabled}
          />
        );
      case 'address':
        return (
          <AddressField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
            label={resolvedLabel}
            helperText={resolvedHelperText}
          />
        );
      default:
        return null;
    }
  }
};
